import { ReactNode, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

export const GUIDE_BUTTON_LABEL = "Report documentation";
export const GUIDE_TITLE = "Strategy Report — Documentation";

/**
 * The Strategy Report's user documentation (task 6527): a book icon in the
 * configuration card's top-right corner that opens one large, scrolling
 * dialog. Written for finance professionals new to the report, in plain
 * language with a short formula where one helps, and kept to under ten
 * minutes' reading.
 *
 * The copy restates behaviour whose home is elsewhere -- the formulas in
 * docs/backend/bucket_performance_calculations.md, the settings in
 * StrategyReportSettings, the exports in ReportActions -- so re-read the
 * affected paragraph when one of those changes.
 */

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="h5"
    component="h2"
    sx={{ color: "#1B4F72", fontWeight: "bold", mt: 1, mb: 1.5 }}
  >
    {children}
  </Typography>
);

const Topic = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box component="section" sx={{ mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 0.5 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

// A bulleted list of "Term — explanation" lines.
const Terms = ({ items }: { items: [ReactNode, ReactNode][] }) => (
  <Box component="ul" sx={{ mt: 0, mb: 1, pl: 3 }}>
    {items.map(([term, text], index) => (
      <Typography
        key={index}
        component="li"
        variant="body2"
        sx={{ mb: 0.5, lineHeight: 1.6 }}
      >
        <strong>{term}</strong> — {text}
      </Typography>
    ))}
  </Box>
);

const Formula = ({ lines }: { lines: string[] }) => (
  <Box
    component="pre"
    sx={{
      fontFamily: "monospace",
      fontSize: "0.8rem",
      whiteSpace: "pre-wrap",
      backgroundColor: "#F7FCFF",
      border: "1px solid #E0E0E0",
      borderRadius: 1,
      px: 1.5,
      py: 1,
      mt: 0,
      mb: 1,
    }}
  >
    {lines.join("\n")}
  </Box>
);

const SetupSection = () => (
  <>
    <SectionTitle>1. Setting up the report</SectionTitle>
    <Topic title="What the report is for">
      <P>
        The Strategy Report shows how a client&apos;s Optic Equity Access (OEA)
        strategy performed over a chosen period. It answers four questions, one
        card each: did the client <strong>keep</strong> their stock, how well
        were they <strong>protected</strong> against large declines, how far did
        they <strong>diversify</strong> away from a single stock, and what tax
        opportunities were <strong>harvested</strong>. A performance chart and a
        summary above the cards give the returns and values of each part of the
        strategy and of the whole portfolio.
      </P>
    </Topic>
    <Topic title="Client and account">
      <P>
        Search for the client in <strong>Client</strong>. The report runs on one
        account: a client with a single account has it selected automatically;
        otherwise pick the account from the chips below the client&apos;s name.
        The <strong>Go to Client</strong> icon beside the name opens the
        client&apos;s page.
      </P>
    </Topic>
    <Topic title="Buckets — how accounts map to the strategy">
      <P>The strategy is split into three buckets, each measured on its own:</P>
      <Terms
        items={[
          [
            "Bucket 1",
            "the client's concentrated stock position(s), held in the selected account and protected with option collars. The Keep and Protect cards are built from it.",
          ],
          [
            "Bucket 2",
            "other securities of the same account, including any option income on them. Bucket 1 and Bucket 2 never share a security.",
          ],
          [
            "Bucket 3",
            "the tax-loss harvest sleeve: a bucket on any of the client's accounts, often a different one from Buckets 1 and 2. Its return is measured on that account's whole value, net of deposits and withdrawals.",
          ],
        ]}
      />
      <P>
        Click the pencil beside Bucket 1 or Bucket 2 to choose its securities.{" "}
        <em>Not created</em> means the account has no such bucket yet;{" "}
        <em>Empty</em> means it has no securities. Hover over a security to see
        whether it was held during the period; one that was not is disregarded.
        For Bucket 3, pick the account in the drop-down; accounts without a
        Bucket 3 are marked <em>no Bucket 3</em>.
      </P>
    </Topic>
    <Topic title="Reporting period">
      <P>
        <strong>Period</strong> offers four options. Start dates move to the
        next weekday and end dates to the previous weekday, and the option you
        used last is remembered.
      </P>
      <Terms
        items={[
          [
            "Year",
            "a calendar year; the current year runs to the last weekday before today.",
          ],
          [
            "Month",
            "a calendar month; the current month runs to the last weekday before today.",
          ],
          ["Range", "any start and end date you choose."],
          [
            "Bucket Inception",
            "from the buckets' earliest inception date to the previous business day. Unavailable (hover for why) until an account is picked and a bucket has an inception date.",
          ],
        ]}
      />
    </Topic>
    <Topic title="Report settings (the gear)">
      <P>
        The gear in the card&apos;s bottom-right corner opens{" "}
        <strong>Report Settings</strong>, applied at the next Calculate.
      </P>
      <Terms
        items={[
          [
            "Summary — Time-Weighted Return",
            "how Bucket 1 and 2 returns are measured. Daily Weight Adjustment (the default) treats the bucket as a basket of holdings, each a stock with its options; Bucket Market Value treats it as one holding. Switching updates the Summary and chart at once; the Diversify card and Backend Figures workbook follow at the next Calculate.",
          ],
          [
            "Summary — Option Pairing",
            "which options count as still open, and so carry a market value. Fixed pairing (on, the default) reads each option by its type; Previous formula (off) counts only net-sold options.",
          ],
          [
            "Tax Savings",
            "the state rate follows the client's state (Details tab) and its rate in Settings; the federal rate is entered here, 17% by default. Both feed the Keep card's Tax Savings.",
          ],
          [
            "Bucket 3 Income",
            "the transaction types counted as Bucket 3's Income Received. Dividends only by default.",
          ],
        ]}
      />
      <Formula
        lines={[
          "Daily Weight Adjustment",
          "  Day return = Σ (holding's share of bucket value at prior close × holding return)",
          "Bucket Market Value",
          "  Day return = (Value today − Share flows + Cashflows) ÷ Value yesterday − 1",
          "Both: TWR = (1 + r₁) × (1 + r₂) × … × (1 + rₙ) − 1",
        ]}
      />
    </Topic>
    <Topic title="Calculating">
      <P>
        Press <strong>Calculate</strong> once the client, account and period are
        set. Changing the account, period, Bucket 3 selection, a tax rate or
        Option Pairing clears the report on screen. Editing a bucket&apos;s
        securities or the Bucket 3 Income types does <strong>not</strong> —
        press Calculate again after either, or the figures shown are stale.
      </P>
    </Topic>
  </>
);

const SectionsSection = () => (
  <>
    <SectionTitle>2. Reading the report</SectionTitle>
    <Topic title="Bucket Performance chart">
      <P>
        The chart overlays each selected line&apos;s cumulative return from 0%
        on the period&apos;s first day. Two drop-downs choose the lines:{" "}
        <strong>Bucket 1</strong> (All Securities, Only Stocks, or a single
        security) and <strong>Tax-Loss Harvest</strong> (B2: and B3: lines;
        Bucket 3 offers its whole bucket only). All Securities starts ticked;
        tick as many as you like. The PDF prints the lines shown.
      </P>
    </Topic>
    <Topic title="Summary">
      <P>
        The drop-down beside <strong>Summary</strong> picks the scope. Every
        scope shows the same six cards:
      </P>
      <Terms
        items={[
          [
            "Starting Value / Ending Value",
            "the first and last priced values of the period.",
          ],
          ["Net Change", "Ending Value − Starting Value, in dollars."],
          [
            "Time-Weighted Return",
            "the return with money added or withdrawn taken out. The corner menu restates it per year as CAGR or AAGR; periods shorter than a year show N/A.",
          ],
          [
            "Income Received",
            "the income the scope received in the period; what counts differs by scope (below).",
          ],
          ["Margin Interest", "interest charged for borrowing on margin."],
        ]}
      />
      <Formula
        lines={[
          "CAGR = (1 + TWR) ^ (365 ÷ days in period) − 1",
          "AAGR = TWR ÷ (days in period ÷ 365)",
        ]}
      />
      <P>Each scope reads the cards its own way:</P>
      <Terms
        items={[
          [
            "Bucket 1",
            "income is dividends only; its option premium pays for the collars and shows on the Protect card as Collar P&L.",
          ],
          ["Bucket 2", "income is dividends plus option premium."],
          [
            "Bucket 3",
            "the harvest account's return, adjusted for cash moved in or out. Income counts the types chosen in Report Settings.",
          ],
          [
            "Long Value",
            "the three buckets added together — the whole portfolio before borrowing, from the period's start date (a bucket not yet started counts as 0). Income adds each bucket's income as above; Margin Interest includes Bucket 3's account when it is a different one.",
          ],
          [
            "Net Value",
            "Long Value less the amount borrowed on margin — what the client owns net of borrowing. Income Received and Margin Interest show N/A here; you can type a figure into either.",
          ],
        ]}
      />
      <Formula lines={["Net Value = Long Value − Amount borrowed on margin"]} />
      <P>
        In a bucket scope, an info icon reading &quot;As of …&quot; on Starting
        or Ending Value means the bucket started late or ended early in the
        period. A scope the calculation produced no figures for is greyed out in
        the drop-down.
      </P>
    </Topic>
    <Topic title="Keep">
      <P>
        Whether the client stayed an owner of their stock. The header picks
        <em> Total</em> or one Bucket 1 stock.
      </P>
      <Terms
        items={[
          [
            "Shares (Start / End), Value (Start / End)",
            "the holding at the start and end of the period.",
          ],
          [
            "Dividends, Basis",
            "dividends received, and the cost basis of the holding.",
          ],
          [
            "Participation Rate",
            "how much of the stock's own move, rise or fall, the client took once options are included. N/A (with the reason) on Total, or when the returns have opposite signs or either is zero.",
          ],
          [
            "Tax Savings",
            "the estimated tax deferred by not selling the stock.",
          ],
        ]}
      />
      <Formula
        lines={[
          "Participation Rate = Return with options ÷ Stock return × 100",
          "Tax Savings = Σ max(unrealized gain, 0) × (federal rate + state rate)",
        ]}
      />
    </Topic>
    <Topic title="Protect">
      <P>
        How well the collars limited declines. The header picks a Bucket 1
        stock.
      </P>
      <Terms
        items={[
          [
            "Max Stock Drop",
            "the largest peak-to-trough fall in the selected stock's value.",
          ],
          [
            "OEA Max Drop (All Buckets)",
            "the same measure for all buckets' combined value.",
          ],
          ["Collar P&L", "Bucket 1's total option cash flows over the period."],
          [
            "Available Margin Balance",
            "entered by you; not saved, and cleared when the inputs change.",
          ],
        ]}
      />
      <Formula
        lines={[
          "Max Drop = −(Peak − Lowest value before recovering to it) ÷ Peak × 100",
          "(the largest such fall; one not yet recovered by period end counts)",
        ]}
      />
      <P>
        The <strong>Drawdown Comparison</strong> chart compares the two drops;
        click it to set its axis limits.
      </P>
    </Topic>
    <Topic title="Diversify">
      <P>
        How far the client moved away from a single stock. The header picks{" "}
        <em>All Stocks</em> or one Bucket 1 stock.
      </P>
      <Terms
        items={[
          [
            "Overall Diversified Performance",
            "the combined return of Buckets 2 and 3, weighted by their starting values.",
          ],
          ["Tax-Loss Harvest Sleeve", "Bucket 3's return."],
          [
            "Beginning Concentration",
            "with All Stocks, a baseline you enter (100% by default); with one stock, that stock's share of Bucket 1's stock value at the start of the period.",
          ],
          [
            "Current Concentration",
            "Bucket 1's stock value (or the picked stock's) as a share of Long Value at period end; it can exceed 100%.",
          ],
          [
            "Holdings",
            "the number of securities held in Buckets 2 and 3 at period end, each counted once; N/A without a Bucket 3.",
          ],
        ]}
      />
      <Formula
        lines={[
          "Current Concentration = Bucket 1 stock value ÷ Long Value × 100",
        ]}
      />
      <P>
        The <strong>Current Allocation</strong> chart shows Current
        Concentration against Diversified Assets for all Bucket 1 stocks.
      </P>
    </Topic>
    <Topic title="Harvest">
      <P>
        Tax opportunities in the Bucket 3 sleeve. Tax figures are estimates.
      </P>
      <Terms
        items={[
          [
            "Realized Losses, Losses Used, Pool Remaining",
            "not calculated yet; they read NOT IMPLEMENTED until you type a figure.",
          ],
          [
            "Unrealized Gains/Losses",
            "the unrealized gain or loss across all three buckets at period end.",
          ],
          [
            "Shares Sold",
            "shares of the Bucket 3 stock picked below sold from Bucket 3 during the period.",
          ],
          [
            "Bucket 3 Stock",
            "picks the stock Shares Sold and Sellable Today describe.",
          ],
          [
            "Sellable Today",
            "shares of it held now, as of the date shown — the account's latest positions, not the period end.",
          ],
        ]}
      />
    </Topic>
  </>
);

const ExportSection = () => (
  <>
    <SectionTitle>3. Exporting and other details</SectionTitle>
    <Topic title="Download Excel">
      <P>The button opens a menu with two workbooks:</P>
      <Terms
        items={[
          [
            "Backend Figures",
            "the workbook the last Calculate produced, with the figures as values. Available once the report is calculated.",
          ],
          [
            "Excel Live Formulas",
            "the same workbook with live formulas in place of values, so you can trace the arithmetic. It is built on request, takes a few minutes, and does not need Calculate first.",
          ],
        ]}
      />
      <P>
        Both hold a sheet per security (e.g. &quot;B1 AAPL&quot;), a total per
        bucket (&quot;B1 Total&quot; ends with the Tax Savings working),
        &quot;Total&quot;, a sheet for each figure that needs its working shown
        (Unrealized GL, Max Drop, Participation Rate, Long &amp; Net Value),
        &quot;Transactions&quot;, and &quot;Issues&quot; listing any security
        that could not be calculated or was not held.
      </P>
    </Topic>
    <Topic title="Download PDF">
      <P>
        A client-ready PDF of the report on screen: the chart as shown, the
        Summary cards for the chosen scope and return measure, and the four
        themed cards with any figures you typed. It is available only once the
        report is calculated for the current inputs.
      </P>
    </Topic>
    <Topic title="Transactions">
      <P>
        <strong>Transactions</strong> lists the buckets&apos; transactions,
        filterable by bucket and security; under All, a Bucket column shows
        which bucket each belongs to. It is read-only here.
      </P>
    </Topic>
    <Topic title="Good to know">
      <Terms
        items={[
          [
            "No Report, N/A and NOT IMPLEMENTED",
            "No Report means nothing is calculated yet; N/A means the figure could not be produced (hover the icon, where shown, for the reason); NOT IMPLEMENTED means the report does not calculate it yet.",
          ],
          [
            "Typed figures",
            "they print on the PDF. One typed over a calculated figure stays in this browser until the next Calculate; one typed into an uncalculated row is dropped when the client changes or the page reloads; Available Margin Balance clears whenever an input changes.",
          ],
          [
            "Failed securities",
            "a warning lists any that could not be calculated; the rest of the report still appears.",
          ],
          [
            "Cached calculations",
            "repeating a calculation is fast. Users with access to Settings › Cache can clear all accounts' cached results with Reset SR Cache.",
          ],
          [
            "Tax figures",
            "Tax Savings and the Harvest card are estimates; review them with a tax advisor.",
          ],
        ]}
      />
    </Topic>
  </>
);

const StrategyReportGuide = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title={GUIDE_BUTTON_LABEL}>
        <IconButton
          aria-label={GUIDE_BUTTON_LABEL}
          size="small"
          onClick={() => setOpen(true)}
        >
          <MenuBookOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        aria-labelledby="strategy-report-guide-title"
      >
        {/* Its own id: without one the title takes aria-labelledby's, and
            the dialog's name would read the subtitle too. */}
        <DialogTitle id="strategy-report-guide-heading" sx={{ pr: 6 }}>
          <span id="strategy-report-guide-title">{GUIDE_TITLE}</span>
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Under 10 minutes to read
          </Typography>
          <IconButton
            aria-label="Close documentation"
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <SetupSection />
          <Divider sx={{ my: 2 }} />
          <SectionsSection />
          <Divider sx={{ my: 2 }} />
          <ExportSection />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StrategyReportGuide;
